import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowRight, Check, Download, Minus, Sparkles, Upload } from 'lucide-react';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';
import comparisonData from '../data/pocket-comparison.json';

const ROUTE = '/pocket';
const OG_IMAGE = 'https://markflow.fyi/og.png';

type CellLabel = { kind: 'label'; tone: 'good' | 'bad' | 'soon'; labelKey: string };
type CellText = { kind: 'text'; text: string };
type CellBool = { kind: 'bool'; value: boolean; note?: string };
type Cell = CellLabel | CellText | CellBool;

type ProductId = 'markflow' | 'pocket' | 'raindrop' | 'instapaper' | 'matter';

export function PocketLandingPage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  const title = t('pocket.meta.title');
  const description = t('pocket.meta.description');
  const ogTitle = t('pocket.meta.ogTitle');
  const ogDescription = t('pocket.meta.ogDescription');

  usePageMeta({
    title,
    description,
    canonical: pageUrl,
    ogTitle,
    ogDescription,
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MarkFlow',
      description: ogDescription,
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web, Chrome, Firefox, Edge, Opera',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
      url: pageUrl,
      image: OG_IMAGE,
    },
  });

  const products = comparisonData.products as { id: ProductId; highlight?: boolean }[];
  const features = comparisonData.features as { key: string; values: Record<ProductId, Cell> }[];

  const productLabel = (id: ProductId) => t(`pocket.products.${id}`);
  const productSubtitle = (id: ProductId) => {
    const key = `pocket.productSubtitles.${id}`;
    const v = t(key);
    return v === key ? null : v;
  };

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-16">

          {/* Hero */}
          <section className="space-y-6 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 dark:bg-violet-950/40 px-3 py-1 text-xs font-medium text-violet-700 dark:text-violet-300">
              <Sparkles className="w-3.5 h-3.5" />
              {t('pocket.hero.eyebrow')}
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50 leading-tight">
              {t('pocket.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              {t('pocket.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link
                href="/?from=pocket"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm w-full sm:w-auto"
              >
                {t('pocket.hero.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/import?variant=pocket"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors w-full sm:w-auto"
              >
                <Upload className="w-4 h-4" />
                {t('pocket.hero.ctaImport')}
              </Link>
            </div>
          </section>

          {/* Trust quote */}
          <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 sm:p-8 space-y-3">
            <blockquote className="text-lg sm:text-xl text-zinc-800 dark:text-zinc-100 leading-relaxed italic">
              {t('pocket.trustQuote.body')}
            </blockquote>
            <p className="text-xs text-zinc-500">{t('pocket.trustQuote.attribution')}</p>
          </section>

          {/* Comparison table */}
          <section className="space-y-4">
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
                {t('pocket.compare.title')}
              </h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {t('pocket.compare.subtitle')}
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left p-3 sm:p-4 font-semibold text-zinc-500 dark:text-zinc-400 min-w-[140px]">
                      {t('pocket.compare.feature')}
                    </th>
                    {products.map((p) => (
                      <th
                        key={p.id}
                        className={`text-left p-3 sm:p-4 font-semibold ${p.highlight ? 'text-violet-700 dark:text-violet-300' : 'text-zinc-900 dark:text-zinc-50'} min-w-[120px]`}
                      >
                        <div>{productLabel(p.id)}</div>
                        {productSubtitle(p.id) && (
                          <div className="text-[10px] uppercase tracking-wide font-medium text-zinc-400 dark:text-zinc-500 mt-0.5">
                            {productSubtitle(p.id)}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((f, i) => (
                    <tr
                      key={f.key}
                      className={i % 2 === 1 ? 'bg-zinc-50/60 dark:bg-zinc-950/30' : ''}
                    >
                      <td className="p-3 sm:p-4 font-medium text-zinc-700 dark:text-zinc-200">
                        {t(`pocket.compare.features.${f.key}`)}
                      </td>
                      {products.map((p) => {
                        const cell = f.values[p.id];
                        return (
                          <td
                            key={p.id}
                            className={`p-3 sm:p-4 text-zinc-600 dark:text-zinc-300 ${p.highlight ? 'bg-violet-50/40 dark:bg-violet-950/20' : ''}`}
                          >
                            {renderCell(cell, t)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-zinc-400 dark:text-zinc-500">
              {t('pocket.compare.disclaimer')}
            </p>
          </section>

          {/* Wedges */}
          <section className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('pocket.wedges.title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['ai', 'crossBrowser', 'privacy'] as const).map((key) => (
                <div
                  key={key}
                  className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 space-y-2"
                >
                  <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                    {t(`pocket.wedges.${key}.title`)}
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    {t(`pocket.wedges.${key}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-500 p-8 sm:p-10 text-center space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              {t('pocket.finalCta.title')}
            </h2>
            <p className="text-violet-100 max-w-lg mx-auto">
              {t('pocket.finalCta.body')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link
                href="/?from=pocket"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-violet-700 bg-white hover:bg-zinc-50 transition-colors shadow-sm w-full sm:w-auto"
              >
                {t('pocket.finalCta.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/download"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white border border-white/40 hover:bg-white/10 transition-colors w-full sm:w-auto"
              >
                <Download className="w-4 h-4" />
                {t('pocket.finalCta.ctaSecondary')}
              </Link>
            </div>
            <p className="text-xs text-violet-100/80">
              {t('pocket.finalCta.note')}
            </p>
          </section>

        </div>
      </main>
    </PublicShell>
  );
}

function renderCell(cell: Cell, t: (k: string) => string) {
  if (cell.kind === 'bool') {
    return (
      <span className="inline-flex items-center gap-1.5">
        {cell.value ? (
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <Minus className="w-4 h-4 text-zinc-400" />
        )}
        {cell.note && (
          <span className="text-[11px] text-zinc-500">({t(`pocket.compare.notes.${cell.note}`)})</span>
        )}
      </span>
    );
  }
  if (cell.kind === 'label') {
    const tones: Record<string, string> = {
      good: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      bad: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
      soon: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[cell.tone]}`}>
        {t(`pocket.compare.labels.${cell.labelKey}`)}
      </span>
    );
  }
  return <span>{cell.text}</span>;
}
